import * as CalendarSchedulesActions from './calendar-schedule.actions';

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {CalendarSchedule, CalendarScheduleSearchParams} from '@accounting/_common/models/calendar.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const calendarSchedulesFeatureKey = 'calendarSchedules';

export function sortByName(b: CalendarSchedule, a: CalendarSchedule) {
    return a.name!.localeCompare(b.name!);
}

export const adapter: EntityAdapter<CalendarSchedule> = createEntityAdapter<CalendarSchedule>({
    sortComparer: sortByName
});

export type CalendarSchedulesState = EntityStateModel<CalendarSchedule, CalendarScheduleSearchParams>;

export const initialState: CalendarSchedulesState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const calendarSchedulesReducer = createReducer(
    initialState,

    on(CalendarSchedulesActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(CalendarSchedulesActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(CalendarSchedulesActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(CalendarSchedulesActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(CalendarSchedulesActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(CalendarSchedulesActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(CalendarSchedulesActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(CalendarSchedulesActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(CalendarSchedulesActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(CalendarSchedulesActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(CalendarSchedulesActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(CalendarSchedulesActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(CalendarSchedulesActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: CalendarScheduleSearchParams | undefined, item: CalendarSchedule): boolean => {
    if (!searchParams) {
        return true;
    }

    return !(!item.name && searchParams.query || searchParams.query && (item.name as string).toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1);
};
